// context/ClientSubscriptionContext.js
// Manages client subscription state and usage tracking across the app

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import { clientSubscriptionService, SUBSCRIPTION_TIERS } from "../services/clientSubscriptionService";
import { useAuth } from "./AuthContext";

const ClientSubscriptionContext = createContext();

export function ClientSubscriptionProvider({ children }) {
  const { user, initialLoadComplete } = useAuth();

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [limitData, setLimitData] = useState(null);

  // Refs to prevent infinite loops and concurrent fetches
  const isFetchingRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const hasErrorRef = useRef(false);
  const retryTimerRef = useRef(null);

  /**
   * Fetch subscription and usage data
   */
  const refreshSubscription = useCallback(async (force = false) => {
    // Early exit if no user or user has no clientId yet (auth still loading)
    if (!user || (user.type === "client" && !user.clientId)) {
      if (!user) {
        setSubscription(null);
        setUsage(null);
      }
      // Always reset tracking refs so we retry when auth resolves
      lastUserIdRef.current = null;
      hasErrorRef.current = false;
      setLoading(false);
      return;
    }

    // Get current user ID (stable reference check) — use clientId as primary key
    const currentUserId = user.clientId || user.id;

    // Skip if already fetching (prevents concurrent requests)
    if (isFetchingRef.current) {
      return;
    }

    // Skip if same user and we already have data (unless forced)
    if (!force && lastUserIdRef.current === currentUserId && (subscription || hasErrorRef.current)) {
      return;
    }

    // Skip if user is not a client type (garages and admins don't need client subscriptions)
    if (user.type && user.type !== "client") {
      setSubscription(null);
      setUsage(null);
      setLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);

      const [subResult, usageResult] = await Promise.all([
        clientSubscriptionService.getSubscription(),
        clientSubscriptionService.getUsage(),
      ]);

      // Update last user ID after successful fetch attempt
      lastUserIdRef.current = currentUserId;

      if (subResult.success) {
        setSubscription(subResult.data);
        hasErrorRef.current = false;
        // Clear any pending retry
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
      } else {
        // Check if this is a transient auth error vs a real "profile missing" error
        const isAuthTransient = subResult.error?.includes("not authenticated") ||
                                 subResult.error?.includes("JWT");
        const isProfileMissing = subResult.error?.includes("profile not found");

        if (isProfileMissing && !isAuthTransient) {
          // Real profile missing — user needs to create profile, don't retry aggressively
          hasErrorRef.current = true;
        }

        // Set free tier defaults on error (only log once)
        if (!hasErrorRef.current || !subscription) {
          console.warn("Client subscription unavailable, using defaults:", subResult.error);
        }
        setSubscription({
          tier: "free",
          status: "active",
          ...SUBSCRIPTION_TIERS.free.limits,
          ...SUBSCRIPTION_TIERS.free.features,
        });

        // Schedule ONE retry after 2s for transient errors (auth not ready yet)
        // This catches the case where subscription fetches before Supabase session is stable
        if (!hasErrorRef.current && !retryTimerRef.current && user?.clientId) {
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            // Reset so the retry can actually run
            lastUserIdRef.current = null;
            refreshSubscription(true);
          }, 2000);
        }
      }

      if (usageResult.success) {
        setUsage(usageResult.data);
      } else {
        // Only log if we haven't already flagged an error
        if (!hasErrorRef.current) {
          console.warn("Client usage unavailable, using defaults:", usageResult.error);
        }
        setUsage({
          ocrScansUsed: 0,
          tripsStarted: 0,
          receiptsStored: 0,
        });
      }

      setError(null);
    } catch (err) {
      console.error("Error refreshing subscription:", err);
      setError(err.message);
      hasErrorRef.current = true;
      // Set defaults on error to prevent further retries
      if (!subscription) {
        setSubscription({
          tier: "free",
          status: "active",
          ...SUBSCRIPTION_TIERS.free.limits,
          ...SUBSCRIPTION_TIERS.free.features,
        });
      }
      if (!usage) {
        setUsage({
          ocrScansUsed: 0,
          tripsStarted: 0,
          receiptsStored: 0,
        });
      }
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [user?.id, user?.clientId, user?.type]); // Only depend on stable user properties

  // Load subscription data when clientId changes (not the whole user object)
  // Wait for initialLoadComplete to ensure auth state is stable before fetching
  // This prevents fetching during transient SIGNED_OUT → SIGNED_IN auth events
  useEffect(() => {
    // Don't fetch until auth has fully resolved
    if (!initialLoadComplete) return;

    const currentUserId = user?.clientId || user?.id;

    // Only fetch if user ID actually changed or we have no data yet
    if (currentUserId !== lastUserIdRef.current || (!subscription && !hasErrorRef.current)) {
      const timer = setTimeout(() => {
        refreshSubscription();
      }, 300);
      return () => {
        clearTimeout(timer);
        // Also clear any pending retry when the effect re-runs
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
      };
    }
  }, [user?.id, user?.clientId, initialLoadComplete, refreshSubscription]);

  /**
   * Check if a feature is available for current tier
   */
  const hasFeature = useCallback(
    (featureName) => {
      if (!subscription) return false;
      return subscription[featureName] ?? false;
    },
    [subscription]
  );

  /**
   * Get current tier info
   */
  const getTierInfo = useCallback(() => {
    if (!subscription) return SUBSCRIPTION_TIERS.free;
    return SUBSCRIPTION_TIERS[subscription.tier] || SUBSCRIPTION_TIERS.free;
  }, [subscription]);

  /**
   * Check OCR limit and show upgrade modal if exceeded
   * Returns true if scan is allowed, false if blocked
   */
  const checkOcrLimitWithPrompt = useCallback(async () => {
    const result = await clientSubscriptionService.checkOcrLimit();

    if (!result.success) {
      console.error("Failed to check OCR limit:", result.error);
      return true; // Allow on error
    }

    if (!result.data.canScan) {
      setLimitData(result.data);
      setUpgradeReason("ocr_limit");
      setShowUpgradeModal(true);
      return false;
    }

    return true;
  }, []);

  /**
   * Check trip limit and show upgrade modal if exceeded
   * Returns true if trip is allowed, false if blocked
   */
  const checkTripLimitWithPrompt = useCallback(async () => {
    const result = await clientSubscriptionService.checkTripLimit();

    if (!result.success) {
      console.error("Failed to check trip limit:", result.error);
      return true; // Allow on error
    }

    if (!result.data.canStartTrip) {
      setLimitData(result.data);
      setUpgradeReason("trip_limit");
      setShowUpgradeModal(true);
      return false;
    }

    return true;
  }, []);

  /**
   * Show upgrade modal for a specific reason
   */
  const promptUpgrade = useCallback((reason, data = null) => {
    setUpgradeReason(reason);
    setLimitData(data);
    setShowUpgradeModal(true);
  }, []);

  /**
   * Close upgrade modal
   */
  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeReason(null);
    setLimitData(null);
  }, []);

  /**
   * Get usage stats with percentages
   */
  const getUsageStats = useCallback(() => {
    if (!subscription || !usage) {
      return {
        ocr: { used: 0, limit: 4, remaining: 4, percent: 0 },
        trips: { used: 0, limit: 20, remaining: 20, percent: 0, unlimited: false },
      };
    }

    const ocrLimit = subscription.ocrScansPerMonth || 4;
    const tripsLimit = subscription.tripsPerMonth;
    const ocrUsed = usage.ocrScansUsed || 0;
    const tripsUsed = usage.tripsStarted || 0;

    return {
      ocr: {
        used: ocrUsed,
        limit: ocrLimit,
        remaining: Math.max(0, ocrLimit - ocrUsed),
        percent: Math.round((ocrUsed / ocrLimit) * 100),
      },
      trips: {
        used: tripsUsed,
        limit: tripsLimit,
        remaining: tripsLimit ? Math.max(0, tripsLimit - tripsUsed) : null,
        percent: tripsLimit ? Math.round((tripsUsed / tripsLimit) * 100) : 0,
        unlimited: tripsLimit === null,
      },
    };
  }, [subscription, usage]);

  /**
   * Refresh usage after an action (OCR scan, trip start)
   */
  const refreshUsage = useCallback(async () => {
    if (!user) return;

    try {
      const result = await clientSubscriptionService.getUsage();
      if (result.success) {
        setUsage(result.data);
      }
    } catch (err) {
      console.warn("Failed to refresh usage:", err);
    }
  }, [user]);

  const value = {
    // State
    subscription,
    usage,
    loading,
    error,
    showUpgradeModal,
    upgradeReason,
    limitData,

    // Actions
    refreshSubscription,
    refreshUsage,
    hasFeature,
    getTierInfo,
    checkOcrLimitWithPrompt,
    checkTripLimitWithPrompt,
    promptUpgrade,
    closeUpgradeModal,
    getUsageStats,

    // Constants
    TIERS: SUBSCRIPTION_TIERS,
  };

  return (
    <ClientSubscriptionContext.Provider value={value}>
      {children}
    </ClientSubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useClientSubscription() {
  const context = useContext(ClientSubscriptionContext);
  if (!context) {
    throw new Error("useClientSubscription must be used within a ClientSubscriptionProvider");
  }
  return context;
}

export default ClientSubscriptionContext;
